import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import style from './NoticesAndBills.module.scss';
import { NoticesBillsTab } from '../../../enums';
import Tabs from '../../../components/Tabs/Tabs';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import AssessmentNoticeTable from '../../../components/AssessmentNoticeTable/AssessmentNoticeTable';
import TaxBillsTable from '../../../components/TaxBillsTable/TaxBillsTable';
import useIncludeReturnsWithNoAssets from '../../../hook/useIncludeReturnsWithNoAssets';
import { useAppDispatch } from '../../../redux/store';
import FieldToggleCheckbox from '../../../components/FieldToggleCheckbox/FieldToggleCheckbox';
import { setIncludeReturnsWithNoAssets } from '../../../redux/features/returns-slice';
import ToastMessage, { ToastMessageRefType } from '../../../components/ToastMessage/ToastMessage';
import { IToastMessage } from '../../../interface';

type Tab = {
  name: string;
  disabled: boolean;
};

function NoticesAndBills() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>(
    searchParams.get('tab') as string,
  );
  const stateIncludeReturnsWithNoAssets = useIncludeReturnsWithNoAssets();
  const [includeReturnsWithNoAsset, setIncludeReturnsWithNoAsset] = useState<boolean>(stateIncludeReturnsWithNoAssets);
  const toastRef = useRef<ToastMessageRefType | null>(null);
  const dispatch = useAppDispatch();
  const [toastMessage, setToastMessage] = useState<IToastMessage>();

  const setTab = useCallback(
    (tab: string) =>
      setSearchParams(new URLSearchParams({tab}), {
        replace: true,
      }),
    [setSearchParams],
  );

  useEffect(() => {
    setTabs(_.map(NoticesBillsTab, (tab) => ({ name: tab, disabled: false })));
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');

    if (!tabParam) {
      setTab(NoticesBillsTab.AssessmentNotices);
    } else if (tabParam !== selectedTab) {
      setSelectedTab(tabParam);
    }
  }, [searchParams, selectedTab, setTab]);

  useEffect(() => {
    if (includeReturnsWithNoAsset !== stateIncludeReturnsWithNoAssets) {
      dispatch(setIncludeReturnsWithNoAssets(includeReturnsWithNoAsset));
    }
  }, [includeReturnsWithNoAsset, stateIncludeReturnsWithNoAssets, dispatch]);

  const breadcrumb = [
    {
      value: 'Taxes',
      href: '/taxes',
    },
    { value: 'Manage Taxes' },
  ];

  return (
      <div className={style['main-content']}>
        <Breadcrumb items={breadcrumb} />
        <ToastMessage
          ref={toastRef}
          status={toastMessage}
        />
        <h2>Notices and Bills</h2>
        <div className={style['main']}>
          <FieldToggleCheckbox
            label='Include Returns with no assets'
            value={includeReturnsWithNoAsset}
            onChange={(value) => setIncludeReturnsWithNoAsset(value)}
            classnames={style['float-right']}
          />
          {selectedTab && (
            <Tabs tabs={tabs} selected={selectedTab} onSelect={(x) => setTab(x)}>
              {_.isEqual(selectedTab, NoticesBillsTab.AssessmentNotices) ? (
                <AssessmentNoticeTable
                  includeReturnsWithNoAssets={includeReturnsWithNoAsset}
                  toastRef={toastRef}
                />
              ) : (
                <TaxBillsTable
                  includeReturnsWithNoAssets={includeReturnsWithNoAsset}
                  toastRef={toastRef}
                />
              )}
            </Tabs>
          )}
        </div>
      </div>
  );
}

export default NoticesAndBills;
